import { getPet } from 'src/game/pet';
import { trackPetSpawn } from 'src/lib/analytics/events';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { waitForAssets, waitForItPromise } from 'src/lib/utils';
import { petCrateAssets, petSpeciesAssets } from 'src/loadingGroups';
import * as Pet from 'src/replicant/pet';
import { TutorialStep } from 'src/replicant/tutorial/tutorial.ruleset';
import { setLockMenu, setPetCrateOpening } from 'src/state/ui';
import StateObserver from 'src/StateObserver';
import { appendToConversationData } from 'src/game/conversation/conversation.utils';
import { Feature } from 'src/replicant/analytics';
import { getHomePetCrate } from 'src/game/pet/pet.utils';
import {
  TutorialEvent,
  getTutorialController,
} from 'src/game/tutorial/TutorialController';
import OrdinaryTutorial from 'src/game/tutorial/handlers/OrdinaryTutorial';
import { registerHandTarget } from 'src/game/tutorial/services/TutorialHandService';
import { showTutorial } from 'src/game/tutorial/tutorial.util';
import {
  puzzleCoinsGetStepProps,
  puzzleCoinsTrackStepFinish,
  puzzleCoinsTrackStepStart,
} from '../puzzlecoins.tracking';
import features from 'src/replicant/ruleset/features';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';

export class PuzzleCoinsBasicTutorial extends OrdinaryTutorial {
  public readonly step: TutorialStep = 'puzzleCoinsBasic';

  public async handleEvent(event: TutorialEvent): Promise<boolean> {
    // Event not relevant to this tutorial.
    if (event !== 'gameStarted') return false;
    let { user } = StateObserver.getState();

    // If pet doesn't exist yet
    if (!Pet.getters.getCurrentEvolution(user)) {
      await this.createFirstPet();
    }

    // Prepare pet
    await this.initPet();

    return true;
  }

  /**
   * Old first pet creation flow
   */
  private async createFirstPet() {
    let { user } = StateObserver.getState();

    // Block the menus - they would be insteactive for a very short time during the pet creation
    StateObserver.dispatch(setLockMenu(true));

    // In demo mode we stop game here and require for login
    if (
      features(user).isDemoMode &&
      isDynamicTestInBucket(user, ab.tests.TEST_DEMO_MODE, 'afterFirstPuzzle')
    ) {
      await openPopupPromise('popupLineDemoLogin', {});
      return;
    }

    StateObserver.dispatch(setPetCrateOpening(true));

    // Open box step
    puzzleCoinsTrackStepStart('openBox');
    await waitForAssets(petCrateAssets, {
      feature: Feature.TUTORIAL._,
      subFeature: Feature.TUTORIAL.CRATE_ASSETS,
    });

    const petCrate = getHomePetCrate();

    await openPopupPromise('popupOpenCrate', {});

    await StateObserver.invoke.createNewPet();
    await StateObserver.invoke.schedulePetActivity();
    puzzleCoinsTrackStepFinish('openBox');

    // Pet spawn
    trackPetSpawn({ feature: 'new_game', subFeature: 'new_game' });
    user = StateObserver.getState().user;
    const { species } = Pet.getters.getCurrentEvolution(user)!;
    await waitForAssets(petSpeciesAssets[species], {
      feature: Feature.TUTORIAL._,
      subFeature: Feature.TUTORIAL.PET_ASSETS,
    });
    appendToConversationData(species);
    petCrate.play('pet_crate_open', () => {
      petCrate.removeFromSuperview();
      StateObserver.dispatch(setPetCrateOpening(false));
    });

    // Show pet during crate opening animation
    await waitForItPromise(1250);
  }

  /**
   * Old first pet initialization flow
   */
  private async initPet() {
    const { user } = StateObserver.getState();
    const pet = getPet();

    // Ensure register of pet hand target for tutorials
    registerHandTarget('buruburu', pet.entity.getView());

    const { species } = Pet.getters.getCurrentEvolution(user)!;

    // Make sure assets are loaded.
    await waitForAssets(petSpeciesAssets[species], {
      feature: Feature.TUTORIAL._,
      subFeature: Feature.TUTORIAL.PET_ASSETS,
    });
    appendToConversationData(species);

    // Set pet species.
    pet.appearance.setSpecies(species);

    // Make sure pet is visible.
    pet.show();

    // Enable pet conversations and show initial greetings
    await pet.startConversation('initial_greetings');
    pet.conversation.start();

    pet.position.setWandering(true);

    const props = puzzleCoinsGetStepProps('basicGameplay');

    await showTutorial('puzzleCoinsBasic', {
      $stepIndex: props.stepIndex,
      $stepName: props.stepName,
    });

    // Unlock the menus
    StateObserver.dispatch(setLockMenu(false));

    getTutorialController().handleEvent('puzzleCoinsBasicStepComplete');
  }
}
